import {
  type DatePickerProps as BaseDatePickerProps,
  type TimeInputProps,
  DatePicker as BaseDatePicker,
  TimeInput,
} from '@mantine/dates';
import { useMergedRef } from '@mantine/hooks';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import { alpha, styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { forwardRef, useEffect, useRef, useState } from 'react';
import 'dayjs/locale/th';

import { Flex } from '../../flex';
import { Grid } from '../../grid';
import { AccessTimeIcon, CalendarTodayIcon, CloseRoundedIcon } from '../../icons';
import { ColorPalette } from '../../theme';

type DateTimeInputProps = Omit<BaseDatePickerProps, 'sx'> & {
  defaultTime?: { hour?: number; minute?: number; second?: number };
  helperText?: string;
  TimePickerProps?: Omit<TimeInputProps, 'sx' | 'onChange' | 'color' | 'error'> & {
    onChange?: (value: Date | null) => void;
  };
};

const StyledInputLabel = styled(InputLabel)({
  position: 'absolute',
  top: 0,
  left: 0,
});

const StyledDateInput = styled(BaseDatePicker, {
  shouldForwardProp: (prop) => prop !== 'color' && prop !== 'error',
})<Omit<BaseDatePickerProps, 'sx'> & { color?: ColorPalette; error?: boolean }>(({
  theme,
  color: colorProp,
  error,
}) => {
  const color = (error ? ColorPalette.ERROR : colorProp) ?? ColorPalette.PRIMARY;
  return {
    marginTop: theme.spacing(6),
    border: '1px solid',
    borderColor: error ? theme.palette.error.main : theme.palette.grey['300'],
    borderRadius: theme.shape.rounded(1),
    '& .mantine-DatePicker-day[data-selected="true"]': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.text.primary,
      fontWeight: 600, //  TODO: use theme.typography.fontWeightSemibold
    },
    input: {
      fontFamily: theme.typography.fontFamily,
      color: theme.palette.text.primary,
      fontSize: theme.typography.fontSize,
      lineHeight: 1.5,
      fontWeight: theme.typography.fontWeightRegular,
      border: 'none',
      boxSizing: 'border-box',
      minHeight: '42px',
      padding: '16.5px 14px',
      height: '1.4375em',
      ':hover': { borderColor: theme.palette[color].main },
      ':focus': {
        borderColor: theme.palette[color].main,
        boxShadow: `${alpha(theme.palette[color].main, 0.25)} 0 0 0 3px`,
      },
      ':disabled': {
        opacity: 1,
        cursor: 'default',
        color: theme.palette.text.disabled,
        backgroundColor: '#F1F3F5',
        ':hover': { borderColor: theme.palette.grey['300'] },
        '::placeholder': { color: theme.palette.text.disabled },
      },
    },
  };
});

const StyledTimeInput = styled(TimeInput, {
  shouldForwardProp: (prop) => prop !== 'color' && prop !== 'error',
})<Omit<TimeInputProps, 'sx'> & { color?: ColorPalette; error?: boolean }>(({
  theme,
  color: colorProp,
  error,
}) => {
  const color = (error ? ColorPalette.ERROR : colorProp) ?? ColorPalette.PRIMARY;
  return {
    marginTop: theme.spacing(6),
    border: '1px solid',
    borderColor: error ? theme.palette.error.main : theme.palette.grey['300'],
    borderRadius: theme.shape.rounded(1),
    'input, .mantine-Text-root': {
      color: theme.palette.text.primary,
      fontFamily: theme.typography.fontFamily,
      fontSize: 16,
      lineHeight: 1.5,
      fontWeight: 400,
    },
    'input::placeholder, .mantine-Text-root': {
      color: theme.palette.text.disabled,
      fontSize: 18,
      textAlign: 'center',
    },
    ':has(input[value=""]) .mantine-Text-root': {
      color: `${theme.palette.text.disabled} !important`,
    },
    '.mantine-TimeInput-input': {
      paddingLeft: 10,
      border: 'none',
      minHeight: '42px',
      boxSizing: 'border-box',
      height: '1.4375em',
      ':hover': { borderColor: theme.palette[color].main },
      '&.mantine-TimeInput-disabled': {
        backgroundColor: '#F1F3F5',
        ':hover': { borderColor: theme.palette.grey['300'] },
        '&,input:disabled': {
          cursor: 'default',
          opacity: 1,
          color: theme.palette.text.disabled,
        },
      },
      ':has(input:focus)': {
        borderColor: theme.palette[color].main,
        boxShadow: `${alpha(theme.palette[color].main, 0.25)} 0 0 0 3px`,
      },
    },
  };
});

const DateTimeInput = forwardRef((props: DateTimeInputProps, forwardedRef) => {
  const {
    label,
    value,
    onChange,
    helperText,
    disabled: isDisabled,
    error,
    color,
    style,
    defaultTime,
    clearable: isClearable = true,
    TimePickerProps,
    ...restProps
  } = props;

  const DateTimeInputRef = useRef<HTMLInputElement>(null);
  const ref = useMergedRef(DateTimeInputRef, forwardedRef);
  const [date, setDate] = useState<Date | null>(value ? new Date(value) : null);

  const {
    hour: hourProp = 23,
    minute: minuteProp = 59,
    second: secondProp = 59,
  } = defaultTime ?? {};

  useEffect(() => {
    setDate(value ? new Date(value) : null);
  }, [value]);

  const handleDateChange = (value: Date | null) => {
    const hour = dayjs(date).get('hour');
    const minute = dayjs(date).get('minute');

    const dateWithChange = date
      ? dayjs(value).set('hour', hour).set('minute', minute).toDate()
      : dayjs(value)
          .set('hour', hourProp)
          .set('minute', minuteProp)
          .set('second', secondProp)
          .toDate();

    setDate(dateWithChange);
    onChange?.(dateWithChange);
  };

  const handleTimeChange = (value: Date | null) => {
    let dateWithChange;

    if (date) {
      const newHour = dayjs(value).get('hour');
      const newMinute = dayjs(value).get('minute');

      dateWithChange = dayjs(date).set('hour', newHour).set('minute', newMinute).toDate();
    } else {
      dateWithChange = value;
    }

    setDate(dateWithChange);
    onChange?.(dateWithChange);
  };

  const handleClear = () => {
    setDate(null);
    onChange?.(null);
  };

  const rightButton =
    !isClearable || !date ? (
      <CalendarTodayIcon sx={{ color: 'text.disabled', pointerEvents: 'none', fontSize: 24 }} />
    ) : (
      <IconButton onClick={handleClear} disabled={props.disabled} sx={{ color: 'text.secondary' }}>
        <CloseRoundedIcon />
      </IconButton>
    );

  return (
    <Grid sx={{ position: 'relative' }}>
      <Flex sx={{ gap: 2, flexWrap: 'wrap', position: 'relative' }}>
        {label ? <StyledInputLabel required={props.required}>{label}</StyledInputLabel> : null}
        <StyledDateInput
          ref={ref}
          value={date}
          locale='th'
          labelFormat='MMMM YYYY'
          inputFormat='YYYY-MM-DD'
          yearLabelFormat='YYYY'
          size='md'
          color={color as ColorPalette}
          rightSection={rightButton}
          rightSectionProps={{ style: { width: 0, right: 24, top: 3 } }}
          onChange={handleDateChange}
          error={!!error}
          disabled={isDisabled}
          dateParser={(dateString) => new Date(Date.parse(dateString))}
          firstDayOfWeek='sunday'
          style={{ width: 200, ...style }}
          clearable={isClearable}
          {...restProps}
        />
        <StyledTimeInput
          value={date}
          color={color as ColorPalette}
          size='md'
          rightSection={
            <AccessTimeIcon
              sx={{
                color: !isDisabled && date ? 'text.secondary' : 'text.disabled',
                pointerEvents: 'none',
                fontSize: 24,
              }}
            />
          }
          rightSectionProps={{
            style: { width: 0, right: 24 },
          }}
          onChange={handleTimeChange}
          error={!!error}
          disabled={isDisabled}
          {...TimePickerProps}
          style={{ width: 140, ...TimePickerProps?.style }}
        />
      </Flex>
      <FormHelperText error={!!error}>{helperText}</FormHelperText>
    </Grid>
  );
});

DateTimeInput.displayName = 'DateTimeInput';

export { DateTimeInput };
export type { DateTimeInputProps };
